import { Children } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';

import LdsIcon from '../LdsIcon/index.jsx';

export default function LdsLink({
  children,
  ariaLabel = undefined,
  classes = undefined,
  clearDefaultClasses = false,
  contextualIcon = false,
  disableAutoAriaLabel = false,
  disableAutoIcon = false,
  disableButtonIconMargin = false,
  disableIconSpace = false,
  downloadable = false,
  external = false,
  href = undefined,
  icon = undefined,
  iconClasses = undefined,
  iconDescription = undefined,
  iconFill = undefined,
  iconLabel = undefined,
  iconOnly = false,
  iconPosition = 'after',
  mode = 'link',
  onClick,
  target = undefined,
  targetAriaLabelContent = '(opens in a new tab)',
  useIcon = false,
  className,
  ...rest
}) {
  // Determine whether to display an "Easy Icon"
  // Note that "disableAutoIcon" only functions when downloadable or external are set
  // Using the "useIcon" prop should force the icon to appear
  let displayIcon = false;
  if (useIcon || (downloadable && !disableAutoIcon) || (external && !disableAutoIcon)) displayIcon = true;

  const IconElement = () => {
    // Render nothing if no icon should be displayed
    if (!displayIcon) return '';

    // Otherwise, Get icon to use
    // Default to Link so that something always renders
    let name = 'Link';
    // If the link is flagged external, default to the "external link" icon
    if (external) name = 'ArrowSquareOut';
    // If the link is fladded downloadable, default to the "download" icon (supersedes "external")
    if (downloadable) name = 'DownloadSimple';
    // If the icon name is specified, use that (supersedes all defaults)
    if (icon) name = icon;

    let usePositionClass = true;
    if (iconOnly || disableButtonIconMargin) usePositionClass = false;

    // Render the LDS Icon element (uses the base package web component)
    return (
      <LdsIcon
        name={name}
        className={cn(
          {
            'button-icon': mode === 'button',
            [`${iconPosition}`]: usePositionClass,
            [`${iconClasses}`]: iconClasses,
          }
        )}
        decorative={contextualIcon}
        description={iconDescription}
        fill={iconFill}
        inline
        label={iconLabel}
      />
    );
  };

  // Creates a spacer element to add a single text non-breaking space character
  // between the "Easy Icon" and the text of the link
  const IconSpacer = () => {
    // If the developer explicity disabled the icon spacer, do it
    if (disableIconSpace) return '';
    // If it's an "icon only" link (or button link) there should never be an additional space character
    if (iconOnly) return '';
    // When using a button link and explicitly NOT disabling the button icon margin, omit the space
    if (mode === 'button' && !disableButtonIconMargin) return '';
    // Otherwise there should usually be a space character between the link content and the easy icon
    return (<span className="lds-link-icon-spacer">&nbsp;</span>);
  };

  // Discern a file type extension value from the link HREF when flagged as downloadable
  let downloadableFileType;
  if (downloadable) {
    const filePathParts = href.split('.');
    if (filePathParts.length > 1) {
      downloadableFileType = `(${filePathParts.pop().toUpperCase()})`;
    }
  }

  const DownloadableExtension = () => {
    if (!downloadable || !downloadableFileType) return '';
    return (
      <span className="lds-link-downloadable-file-ext">
        &nbsp;{downloadableFileType}
      </span>
    );
  };

  // Attempt to auto-generate a good aria-label attribute from the React children object
  // And any other relevant component prop settings
  // If no linkText is found, no auto aria-label value will be set and the aria-label attribute will not be rendered
  // Also if, by explicit props the auto aria-label function is disabled OR the developer specifies ARIA label content, this will be skipped
  let autoMagicAriaLabel;
  if (!disableAutoAriaLabel && !ariaLabel) {
    // Attempt to get the link text
    // Loops through each element of the React "children" object
    // Plain text children have no type (undefined) so this assumes a falsey child type is plain text
    // Concatenates all found plain text elements together
    let linkText;
    Children.forEach(children, (child) => {
      if (!child.type) {
        if (!linkText) {
          linkText = child;
        } else {
          linkText += ` ${child}`;
        }
      }
    });
    // If there was discernable Link Text then handle other relevant prop modifiers
    if (linkText) {
      // Set the text content
      autoMagicAriaLabel = linkText;
      // Append the downloadable file's extension from the HREF if it can be discerned
      if (downloadable && downloadableFileType) {
        autoMagicAriaLabel += ` ${downloadableFileType}`;
      }
      // Append the target context content when the target is set
      if (target) autoMagicAriaLabel += ` ${targetAriaLabelContent}`;
    }
  }
  // If explcit ARIA label content is defined by the user, always render that instead
  if (ariaLabel) autoMagicAriaLabel = ariaLabel;

  // Render the link
  return (
    <a
      onClick={onClick}
      href={href}
      className={cn(
        {
          'lds-link': mode !== 'button' && !clearDefaultClasses,
          'lds-button': mode === 'button' && !clearDefaultClasses,
          [`${classes}`]: classes,
          'icon-only': iconOnly,
        },
        className
      )}
      target={target}
      aria-label={autoMagicAriaLabel}
      {...rest}
    >
      {displayIcon && iconPosition === 'before' && <IconElement />}
      {displayIcon && iconPosition === 'before' && <IconSpacer />}
      {children}
      {downloadable && <DownloadableExtension />}
      {displayIcon && iconPosition === 'after' && <IconSpacer />}
      {displayIcon && iconPosition === 'after' && <IconElement />}
    </a>
  );
}

LdsLink.propTypes = {
  ariaLabel: PropTypes.string,
  classes: PropTypes.string,
  clearDefaultClasses: PropTypes.bool,
  contextualIcon: PropTypes.bool,
  disableAutoAriaLabel: PropTypes.bool,
  disableAutoIcon: PropTypes.bool,
  disableButtonIconMargin: PropTypes.bool,
  disableIconSpace: PropTypes.bool,
  downloadable: PropTypes.bool,
  external: PropTypes.bool,
  href: PropTypes.string,
  icon: PropTypes.string,
  iconClasses: PropTypes.string,
  iconDescription: PropTypes.string,
  iconFill: PropTypes.string,
  iconLabel: PropTypes.string,
  iconPosition: PropTypes.oneOf(['before', 'after']),
  mode: PropTypes.oneOf(['link', 'button']),
  target: PropTypes.oneOf(['_self', '_blank', '_parent', '_top']),
  targetAriaLabelContent: PropTypes.string,
  useIcon: PropTypes.bool,
};
